import { TelnyxErrorCode } from "@redotech/redo-model/text-messaging/telnyx-types";

const DEFAULT_ERROR_MESSAGE =
  "We couldn't send your message. Please try again or contact Redo support";
const CONTACT_SUPPORT = "Please contact Redo support";

/**
 * Maps Telnyx error codes to user-friendly descriptions
 * Based on documentation: https://support.telnyx.com/en/articles/6505121-telnyx-messaging-error-codes
 * Only includes 4**** error codes (messaging-related errors)
 */
export const telnyxErrorDescriptions: Record<string, string> = {
  [TelnyxErrorCode.NotRoutable]: "Recipient can't receive text messages",
  [TelnyxErrorCode.BlockedAsSpamTemporary]:
    "Message blocked as spam. Try again later",
  [TelnyxErrorCode.BlockedAsSpamPermanent]:
    "Message blocked as spam. Revise content or contact support",
  [TelnyxErrorCode.RejectedByDestination]:
    "Carrier rejected this message. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.MessageExpiredDuringTransmission]:
    "Message timed out. Please try again",
  [TelnyxErrorCode.RecipientServerUnavailable]:
    "Carrier is unavailable. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.LoopDetected]: "Delivery issue detected. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.Undeliverable]: "Message can't be delivered to this number",
  [TelnyxErrorCode.InvalidMessageBody]:
    "Message content rejected. Please revise and try again",
  [TelnyxErrorCode.Unregistered10DLCMessage]:
    "Messaging registration issue. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.TooManyRequestsUpstream]:
    "Too many messages sent recently. Try again later",
  [TelnyxErrorCode.InvalidMessagingDestinationNumber]:
    "Invalid recipient number. Please check and try again",
  [TelnyxErrorCode.InvalidMessagingSourceNumber]:
    "Sender number issue. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.MessageExpiredInQueue]:
    "Message expired in queue. Please try again",
  [TelnyxErrorCode.BlockedAsSpamInternal]:
    "Message flagged as spam. Review content and try again",
  [TelnyxErrorCode.TMobile10DLCSendingLimitReached]:
    "T-Mobile limit reached. Try again tomorrow",
  [TelnyxErrorCode.ATT10DLCSpamMessageRejected]:
    "AT&T rejected as spam. Please revise content",
  [TelnyxErrorCode.ATT10DLCSendingLimitReached]:
    "AT&T limit reached. Try again tomorrow",
  [TelnyxErrorCode.ATT10DLCInvalidTagData]:
    "Message format issue. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.NumberNotMessagingEnabled]:
    "Number not enabled for messaging. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.TollFreeNumberNotInRegistry]:
    "Toll-free number issue. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.MessageEnablementPendingWithOtherProvider]:
    "Number setup pending. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.InvalidOSRParameter]:
    "Technical messaging issue. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.CannotAccessOSR]:
    "Messaging service connection issue. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.UnauthorizedNNID]:
    "Messaging authorization issue. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.LOARequired]:
    "Phone number authorization needed. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.UnauthorizedPropertyNameValue]:
    "Messaging configuration issue. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.TemporarilyBlocked]:
    "Number temporarily blocked. Try again later",
  [TelnyxErrorCode.DeleteFailed]: "Operation failed. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.UnknownOSRError]:
    "Unknown messaging error. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.BlockedDueToSTOPMessage]:
    "Recipient opted out. They must text START to receive messages",
  [TelnyxErrorCode.UnsupportedMessageTypeForToAddress]:
    "Message type not supported for this number",
  [TelnyxErrorCode.MessageTooLarge]:
    "Message too long. Please shorten and try again",
  [TelnyxErrorCode.MessageNotFound]: "Message not found. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.InvalidCombinationOfMessageContentArguments]:
    "Message format issue. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.InvalidFromAddress]:
    "Invalid sender number. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.AlphaSenderNotConfigured]:
    "Sender ID not configured. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.AlphaSenderMismatch]:
    "Sender ID mismatch. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.InvalidFromAddressForMMS]:
    "Sender doesn't support picture messages. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.InvalidDestinationRegion]:
    "Can't send to this region. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.InvalidToAddress]:
    "Invalid recipient number. Please check and try again",
  [TelnyxErrorCode.InvalidMessagingProfileSecret]:
    "Authentication issue. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.MessagingProfileIsDisabled]:
    "Messaging service disabled. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.MissingMessagingProfileSecret]:
    "Messaging configuration issue. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.MessagingDisabledOnAccount]:
    "Account messaging disabled. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.UnhealthyFromAddress]:
    "Sender number unavailable. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.NoContentProvidedForMessage]:
    "Empty message. Please add text and try again",
  [TelnyxErrorCode.InvalidMMSContent]:
    "Invalid image. Please try a different image",
  [TelnyxErrorCode.MessageQueueFull]: "System busy. Please try again later",
  [TelnyxErrorCode.IncompatibleMessageTypeForToAddress]:
    "Message type not supported for this number",
  [TelnyxErrorCode.TemporarilyUnusableFromAddress]:
    "Sender number temporarily unavailable. Try again later",
  [TelnyxErrorCode.NoUsableNumbersOnMessagingProfile]:
    "No available sender numbers. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.BlockedDueToContent]:
    "Message blocked due to content. Please revise and try again",
  [TelnyxErrorCode.MessagingActivationFailed]:
    "Messaging activation failed. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.MessagingProductTypeChangeFailed]:
    "Service type change failed. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.InvalidAlphanumericSenderID]:
    "Invalid sender ID format. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.CannotAssignAlphanumericSenderID]:
    "Can't assign sender ID. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.InvalidDomain]: "Invalid domain. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.SMSExceedsRecommendedSize]:
    "Message too long. Consider shortening it",
  [TelnyxErrorCode.TollfreeNumberNotVerified]:
    "Toll-free number not verified. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.TollfreeNumberNotProvisioned]:
    "Toll-free number not set up. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.MissingWhitelistedDestinations]:
    "Destination restrictions. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.BrandCannotBeDeleted]:
    "Brand deletion issue. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.MessagingProfileSpendLimitReached]:
    "Messaging limit reached. " + CONTACT_SUPPORT,
  [TelnyxErrorCode.WhatsAppError]: "WhatsApp error. " + CONTACT_SUPPORT,
};

function extractTelnyxErrorCode(errorMessage: string): string | null {
  const match = errorMessage.match(/Telnyx failed to send message: (\d+)/);
  return match ? match[1] : null;
}

function getTelnyxErrorDescription(errorCode: string | null): string {
  if (!errorCode) {
    return DEFAULT_ERROR_MESSAGE;
  }

  return telnyxErrorDescriptions[errorCode] || DEFAULT_ERROR_MESSAGE;
}

export function parseTelnyxErrorMessage(
  errorMessage: string | undefined,
): string {
  if (
    !errorMessage ||
    !errorMessage.includes("Telnyx failed to send message")
  ) {
    return DEFAULT_ERROR_MESSAGE;
  }

  const errorCode = extractTelnyxErrorCode(errorMessage);
  const errorDescription = getTelnyxErrorDescription(errorCode);

  return `SMS failed: ${errorDescription}`;
}
